import { useEffect } from "react";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import "react-photo-view/dist/react-photo-view.css";

import Store from "src/Redux/Store";
import { syncI18nLanguageFromStorage } from "src/config/i18n";
import { TourProvider, TourRunner } from "src/Components/UI/Tour";
import { installRuntimeAssetRecovery } from "src/utils/runtimeAssetRecovery";
import { initDesktopNativeNotifications } from "src/Funcs/desktopNotifs";

import "src/index.css";
import "src/tailwindStyles.css";
import "src/Components/Board/1/Financial/Parts/PaymentsList/AddPeyment/AddPeyment.css";
// Always-loaded shared classes used across multiple lazy chunks (see scripts/gen-shared-lazy.mjs).
import "src/shared-lazy.css";

const DEFAULT_LANGUAGE = "en";
const DEFAULT_THEME = import.meta.env.VITE_DEFAULT_THEME || "DarkMode";
const RTL_LANGUAGES = new Set(["fa", "ar"]);

installRuntimeAssetRecovery();
initDesktopNativeNotifications();

function syncThemeColorMeta(rootElement) {
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (!themeColorMeta) return;

  const surfaceColor = getComputedStyle(rootElement)
    .getPropertyValue("--white")
    .trim();
  if (surfaceColor) {
    themeColorMeta.setAttribute("content", surfaceColor);
  }
}

function applyHtmlBootstrapState() {
  if (typeof document === "undefined") return;

  try {
    const rootElement = document.documentElement;
    const language = localStorage.getItem("SiteLanguage") || DEFAULT_LANGUAGE;
    const theme = localStorage.getItem("theme") || DEFAULT_THEME;
    const isRtlLanguage = RTL_LANGUAGES.has(language);

    rootElement.classList.toggle("rtl-Lang", isRtlLanguage);
    rootElement.classList.toggle("ltr-Lang", !isRtlLanguage);
    rootElement.classList.toggle("font-fa", isRtlLanguage);
    rootElement.classList.toggle("font-en", !isRtlLanguage);
    rootElement.setAttribute("theme", theme);
    syncThemeColorMeta(rootElement);
  } catch {
    // Ignore storage access errors (private mode, denied access, ...).
  }
}

export default function Wrapper({ children }) {
  useEffect(() => {
    // Sync language after hydration to avoid SSR mismatch.
    syncI18nLanguageFromStorage();
    applyHtmlBootstrapState();

    // Client-only side effects (PWA SW + runtime reload safety).
    // This must not run on the server.
    if (import.meta.env.PROD) {
      void import("src/config/pwaClient").then(({ registerPwaClient }) => {
        registerPwaClient();
      });
    }

    // Firebase is client-only for the public SSR page.
    void import("src/config/firebase");

    // Capture the install prompt event so a custom install button can be shown.
    function handleBeforeInstallPrompt(e) {
      try {
        e.preventDefault();
      } catch {
        // Ignore errors
      }
      // Store the event for later (e.g., UI can call `prompt()` on it).
      // Expose on `window` so components can access it without extra wiring.
      // Also dispatch a small event that UI can listen to.
      // Some browsers fire this only when the app is installable.
      window.deferredPwaInstallPrompt = e;
      window.dispatchEvent(new CustomEvent("pwa:beforeinstallprompt"));
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  return (
    <Provider store={Store}>
      <TourProvider>
        <TourRunner />
        {children}
      </TourProvider>
      <ToastContainer />
    </Provider>
  );
}
